import { toInteger } from 'lodash-es';
import { memo } from 'react';
import { useStyles } from 'uinix-ui';
import { StyleValue } from 'uinix-ui/lib/types';

import { useTextContent } from '../../../hooks/useTextContent';
import { theme } from '../../../system/theme';
import { testHasOverflownLines } from '../../../utils/testHasOverflownLines';
import { Text } from '../Text';
import type { Props as TextProps } from '../Text';

interface Props extends TextProps {
  /** Number of lines the text truncates after. If omitted, does not truncate. */
  limit?: number;
}

/**
 * `TruncateLines` primarily uses `-webkit-line-clamp` to truncate the text and
 * show the ellipsis after `limit` lines. However `-webkit-line-clamp` is not
 * supported by all browsers. In the case that `-webkit-line-clamp` is not supported,
 * as a fallback, the text truncates after `maxHeight`, which is calculated based on
 * `lineHeight` and/or `variant` line height. The only downside to the fallback approach
 * is that the ellipsis does not render.
 */
export const TruncateLines = memo(
  ({ limit, lineHeight, variant, ...props }: Props) => {
    const styles = useStyles();

    const [ref, textContent] = useTextContent();

    const getMaxHeightNumber = () => {
      if (!limit) return undefined;
      const lineHeightVariant = variant
        ? styles.typography.variants[variant].lineHeight
        : 'm';
      const pxLineHeight =
        lineHeight ?? theme.lineHeights[lineHeightVariant as StyleValue];
      const lineHeightNumber = toInteger(
        (pxLineHeight as string).split('px')[0],
      );
      return limit * lineHeightNumber;
    };

    const getMaxHeight = () => {
      if (!limit) return undefined;
      const maxHeightNumber = getMaxHeightNumber();
      return `${maxHeightNumber}px`;
    };

    const maxHeight = getMaxHeight();
    const maxHeightNumber = getMaxHeightNumber();

    const handleMouseEnter = (event: MouseEvent) => {
      const element = event.target as HTMLElement;
      const childElement = element?.firstChild as HTMLElement;
      if (childElement) {
        childElement.title =
          textContent && testHasOverflownLines(element, maxHeightNumber)
            ? textContent
            : '';
      }
    };

    return (
      <span ref={ref}>
        <Text
          data-eds-styles-disable-interactable
          {...props}
          lineHeight={lineHeight}
          styles={styles.truncateLines}
          styleProps={{ limit, maxHeight }}
          variant={variant}
          onMouseEnter={handleMouseEnter}
        />
      </span>
    );
  },
);
